import { Injectable, InjectionToken, Type, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { Observable, Subject, catchError, map, tap } from 'rxjs';
import { SvcDialogService } from '@solvace/component/svc-dialogs';
import { UserAvatarSize } from '@solvace/component/svc-user-avatar';
import { SvcToastService } from '@solvace/component/svc-toast';
import { AppEnvironmentConfig } from 'projects/config/model/environment.config.model';
import { HttpErrorService } from 'projects/lib-shared-common/src/public-api';
import { SitesService, StaticApplicationId } from 'projects/lib-shared-core/src/public-api';
import { Alert, AlertBodyCreate, AlertData, AlertDrilldownData, AlertDrilldownFilter, AlertListFilter, AlertResponseCreate } from '../interfaces/alert.interface';
import { DialogAlertType } from '../enums/dialog-alert-type.enum';
import { ActionPlanCountersV2Response, AlertV2DrilldownRow, AlertV2ListRow, AlertV2Refs, AlertV2Response } from '../interfaces/alert-v2.interface';

/**
 * A row widget the service refreshes in place. `referenceId` is a plain property on the legacy `svc-*`
 * components and a signal input on the `svz-*` ones, so both shapes are accepted. AB#73413.
 */
export interface AlertRefreshable {
  referenceId?: unknown;
  refresh(): void;
}
import { SvcModalAlertComponent } from '../../svc-modal-alert.component';
import { AlertList } from '../interfaces/alert.interface';
import { AlertAttachmentMedias } from '../interfaces/alert-attachment.interface';

/** Replaces the modal `openAlert` opens, for an app that ships its own alert modal. Unprovided, the shared modal opens. */
export interface SvcModalAlertOverride {
  component: Type<SvcModalAlertComponent>;
  config: Omit<MatDialogConfig, 'data'>;
}

/** Optional: an app provides it to open its own alert modal; every other app keeps `SvcModalAlertComponent`. */
export const SVC_MODAL_ALERT_OVERRIDE = new InjectionToken<SvcModalAlertOverride>('SVC_MODAL_ALERT_OVERRIDE');

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  public alertAdd$ = new Subject<void>();
  public activeCustomProperty: 'alert-list' | 'alert-card' = 'alert-list';
  public alertUpdated$ = new Subject<number>();
  public bookmarkStatusUpdated$ = new Subject<number>();

  private readonly _apiUrlAlert: string = this._appConfig.APIs.apiUrlAlert;
  private readonly _apiBookmark = this._appConfig.APIs.apiUrlBookmark;
  private _totalsStatusComponents: AlertRefreshable[] = [];
  private _bookmarkButtonComponents: AlertRefreshable[] = [];

  private readonly _modalOverride = inject(SVC_MODAL_ALERT_OVERRIDE, { optional: true });

  constructor(
    private _appConfig: AppEnvironmentConfig,
    private _dialogService: SvcDialogService,
    private _http: HttpClient,
    private _sitesService: SitesService,
    private _toastService: SvcToastService,
    private _httpErrorService: HttpErrorService,
    private _translocoService: TranslocoService
  ) {
    this._monitorAlertUpdates();
    this._monitorBookmarkUpdates();
  }

  public registerTotalsStatus(components: AlertRefreshable[]): void {
    this._totalsStatusComponents = components;
  }

  public updateTotalsStatus(components: AlertRefreshable[]): void {
    this._totalsStatusComponents = components;
  }

  public registerBookmarkButtons(components: AlertRefreshable[]): void {
    this._bookmarkButtonComponents = components;
  }

  /**
   * AB#73413 — `referenceId` is a plain property on the legacy `svc-*` components and a signal input on
   * their `svz-*` replacements. Reading it directly compares a function to a number, never matches, and
   * the row silently stops refreshing after a comment or a bookmark change.
   */
  public static referenceIdOf(component: { referenceId?: unknown }): unknown {
    const reference = component?.referenceId;
    return typeof reference === 'function' ? (reference as () => unknown)() : reference;
  }

  private _monitorAlertUpdates(): void {
    this.alertUpdated$.subscribe((alertId) => {
      this._totalsStatusComponents?.find((totalStatus) => AlertService.referenceIdOf(totalStatus) === alertId)?.refresh();
    });
  }

  private _monitorBookmarkUpdates(): void {
    this.bookmarkStatusUpdated$.subscribe((alertId) => {
      this._bookmarkButtonComponents?.find((bookmarkButton) => AlertService.referenceIdOf(bookmarkButton) === alertId)?.refresh();
    });
  }

  private getParams(filter: AlertListFilter, params: HttpParams, key: string): HttpParams {
    const value = filter?.[key];

    if (Array.isArray(value)) value.forEach((val: any) => (params = params.append(`${key}`, val)));
    else params = params.append(key, value);

    return params;
  }

  public getAlertsDataListParams(filter: AlertListFilter | AlertDrilldownFilter): HttpParams {
    let params = new HttpParams();
    Object.keys(filter)?.forEach((key: string) => {
      if (filter?.[key] || key === 'pageIndex' || (key === 'userStatus' && filter?.[key] === false)) params = this.getParams(filter, params, key);
    });

    params = params.append('siteId', this._sitesService.currentSite.siteId);

    return params;
  }

  /**
   * The counters are a full scan of the filtered set and the total does not change while paging, so
   * every page after the first opts out (`frontend-v2-integration.md` §2.1). The caller keeps the
   * total it already has when the response omits it.
   */
  private withCounterOptOut(params: HttpParams, filter: AlertListFilter | AlertDrilldownFilter): HttpParams {
    const isFirstPage = Number(filter?.['pageIndex'] ?? 1) <= 1;
    return isFirstPage ? params : params.append('includeCounters', 'false');
  }

  /**
   * Filtering by favourites means every row returned IS one, so the star is known without asking.
   * Without this each row falls back to its own lookup when `fv` is absent, which is a request per
   * row for an answer the filter already gave. AB#73413.
   */
  private markBookmarkedWhenFiltered(alert: Alert, filter: AlertListFilter): Alert {
    if (!filter?.onlyBookmarked) {
      return alert;
    }
    return { ...alert, alerts: (alert?.alerts ?? []).map((row) => ({ ...row, isBookmarked: true })) };
  }

  /**
   * v2 sends originator, place and failure once per page under `refs` and references them by id.
   * Expanding here keeps every consumer on the shape it already renders, so the payload shrinks
   * without the list, the card or the modal changing. `refs` keys serialise as strings.
   */
  private expandV2List(response: AlertV2Response<AlertV2ListRow>): Alert {
    const refs: AlertV2Refs = response?.refs ?? {};
    return {
      page: response?.page,
      alerts: (response?.data ?? []).map((row) => {
        const person = row.o ? refs.people?.[row.o] : undefined;
        return {
          alertId: row.id,
          alertUniqueId: row.uid,
          siteId: row.s,
          originatorId: row.o,
          originator: person?.n,
          userAvatar: person ? { firstLastName: person.i, preferenceColor: person.c, pictureUrl: person.av, role: person.r } : undefined,
          locationId: row.p,
          location: row.p == null ? undefined : refs.places?.[String(row.p)],
          failureId: row.f,
          failure: row.f == null ? undefined : refs.failures?.[String(row.f)],
          inclusionDate: row.d as unknown as Date,
          isBookmarked: row.fv,
        } as AlertList;
      }),
    };
  }

  private mapDataAlerts(alert: Alert): Alert {
    alert.alerts = alert?.alerts.map((alertList: AlertList) => {
      return {
        ...alertList,
        svcUserAvatar: {
          name: alertList?.originator,
          picture: alertList?.userAvatar?.pictureUrl,
          initialsColor: alertList?.userAvatar?.preferenceColor,
          size: UserAvatarSize.Table,
        },
      };
    });

    return alert;
  }

  public getAlerts(filter: AlertListFilter): Observable<Alert> {
    const params = this.getAlertsDataListParams(filter);

    return this._http
      .get<AlertV2Response<AlertV2ListRow>>(`${this._apiUrlAlert}/v2/Alert`, {
        params: this.withCounterOptOut(params, filter),
      })
      .pipe(map((response) => this.markBookmarkedWhenFiltered(this.mapDataAlerts(this.expandV2List(response)), filter)));
  }

  public getAlertsByType(type: 'alert-list' | 'alert-card', filter: AlertListFilter): Observable<Alert> {
    return type === 'alert-list' ? this.getAlerts(filter) : this.getAlertsCard(filter);
  }

  /**
   * The totals band's breakdown (`frontend-v2-integration.md` §4.1). Site-wide and independent of every
   * list parameter, so it is requested on its own and never alongside a filter or page change.
   */
  public getActionPlanCounters(): Observable<ActionPlanCountersV2Response> {
    return this._http.get<ActionPlanCountersV2Response>(`${this._apiUrlAlert}/v2/Alert/ActionPlanCounters`);
  }

  public deleteAlert(uniqueId: number): Observable<void> {
    return this._http.delete<void>(`${this._apiUrlAlert}/Alert`, {
      params: {
        alertUniqueId: uniqueId,
      },
    });
  }

  private mapDataAlertsCard(alert: Alert): Alert {
    alert.alerts = alert?.alerts?.map((alertList: AlertList) => {
      return {
        ...alertList,
        slides: alertList?.attachments?.map((attachment: AlertAttachmentMedias) => ({
          id: attachment?.attachmentId,
          title: alertList?.failure,
          url: attachment?.signedUrl,
          type: ['.png', '.jpg', '.jpeg', 'png', 'jpg', 'jpeg'].includes(attachment?.extension?.toLowerCase()) ? 'image' : 'video',
        })),
      };
    });

    return alert;
  }

  /**
   * The gallery stays on v1: `/v2/Alert/Card` sends one thumbnail and a count per card, and the
   * redesigned card pages through every photo with arrows and dots (prototype `alertCarousel`), which
   * needs every signed url. AB#74826, owner decision 01/10/2026.
   */
  public getAlertsCard(filter: AlertListFilter): Observable<Alert> {
    const params = this.getAlertsDataListParams(filter);

    return this._http
      .get<Alert>(`${this._apiUrlAlert}/Alert/Card`, { params })
      .pipe(map((alert) => this.markBookmarkedWhenFiltered(this.mapDataAlertsCard(alert), filter)));
  }

  public openAlert(mode: DialogAlertType, id?: number, uniqueId?: number): MatDialogRef<SvcModalAlertComponent> {
    const data = { mode, id, uniqueId };
    if (this._modalOverride) {
      return this._dialogService.open(this._modalOverride.component, { ...this._modalOverride.config, data });
    }

    return this._dialogService.open(SvcModalAlertComponent, {
      data,
      width: '100%',
      maxWidth: '500px',
      panelClass: 'md:py-4',
      autoFocus: false,
    });
  }

  public createOrUpateAlert(alert: AlertBodyCreate): Observable<AlertResponseCreate> {
    return alert?.alertId ? this.updateAlert(alert) : this.createAlert(alert);
  }

  public createAlert(alert: AlertBodyCreate): Observable<AlertResponseCreate> {
    return this._http.post<AlertResponseCreate>(`${this._apiUrlAlert}/Alert`, {
      ...alert,
      siteId: this._sitesService.currentSite.siteId,
    });
  }

  public updateAlert(alert: AlertBodyCreate): Observable<AlertResponseCreate> {
    return this._http.put<AlertResponseCreate>(`${this._apiUrlAlert}/Alert`, {
      ...alert,
      siteId: this._sitesService.currentSite.siteId,
    });
  }

  public getAlertById(alertId: number): Observable<AlertData> {
    return this._http
      .get<AlertData>(`${this._apiUrlAlert}/Alert/ById`, {
        params: {
          alertId,
          siteId: this._sitesService.currentSite.siteId,
        },
      })
      .pipe(
        map((alert) => ({
          ...alert,
          productId: alert.productId ?? alert['productlId'],
        }))
      );
  }

  public getAlertByUniqueId(alertUniqueId: number): Observable<AlertData> {
    return this._http
      .get<AlertData>(`${this._apiUrlAlert}/Alert/ByUniqueId`, {
        params: {
          alertUniqueId,
        },
      })
      .pipe(
        map((alert) => ({
          ...alert,
          productId: alert.productId ?? alert['productlId'],
        }))
      );
  }

  public exportFile(typeFile: 'pdf' | 'excel', filter: AlertListFilter): Observable<HttpResponse<Blob>> {
    const params = this.getAlertsDataListParams(filter);

    return this._http.get(`${this._apiUrlAlert}/alert/download/${typeFile}`, {
      observe: 'response',
      responseType: 'blob',
      params,
    });
  }

  public changeFavorite(isBookmark: boolean, alertId: number): Observable<any> {
    return this.favoriteQuiz(isBookmark, alertId).pipe(
      tap(() => {
        const messageKey = isBookmark ? 'adicionado aos' : 'removido dos';
        this._toastService.success(this._translocoService.translate(`Este quiz foi ${messageKey} favoritos com sucesso`));
      }),
      catchError((err) => {
        this._httpErrorService.showErrorInToast(err);
        return err;
      })
    ) as Observable<any>;
  }

  public favoriteQuiz(isBookmark: boolean, alertId: number): Observable<any> {
    const type = isBookmark ? 'post' : 'delete';

    return this._http.request<any>(type, `${this._apiBookmark}/registryId`, {
      body: {
        registryUniqueId: alertId,
        applicationId: StaticApplicationId.alert,
        siteId: this._sitesService.currentSite.siteId,
      },
    });
  }

  public getAlertDrilldown(filter: AlertDrilldownFilter): Observable<AlertDrilldownData> {
    const params = this.getAlertsDataListParams(filter);

    return this._http.get<AlertDrilldownData>(`${this._apiUrlAlert}/Alert/Drilldown`, {
      params,
    });
  }

  /**
   * v2 of the analytics drilldown (AB#75396): same filters and paging as v1, plus the opening date on every row and
   * the labels under `refs`. Expanded here so the drilldown sheet and the legacy drilldown table keep their shape.
   */
  public getAlertDrilldownV2(filter: AlertDrilldownFilter): Observable<AlertDrilldownData> {
    const params = this.getAlertsDataListParams(filter);

    return this._http
      .get<AlertV2Response<AlertV2DrilldownRow>>(`${this._apiUrlAlert}/v2/Alert/Drilldown`, { params })
      .pipe(map((response) => this.expandV2Drilldown(response)));
  }

  private expandV2Drilldown(response: AlertV2Response<AlertV2DrilldownRow>): AlertDrilldownData {
    const refs: AlertV2Refs = response?.refs ?? {};
    return {
      page: response?.page,
      alerts: (response?.data ?? []).map((row) => ({
        alertId: row.id,
        alertUniqueId: row.uid,
        siteId: row.s,
        locationId: row.p ?? 0,
        location: row.p == null ? '' : (refs.places?.[String(row.p)] ?? ''),
        failureId: row.f ?? 0,
        failure: row.f == null ? '' : (refs.failures?.[String(row.f)] ?? ''),
        createDate: row.d,
      })),
    };
  }

  public getAlertsByComment(filter: AlertListFilter): Observable<Alert> {
    const params = this.getAlertsDataListParams(filter);
    return this._http.get<Alert>(`${this._apiUrlAlert}/Alert/ByComment`, { params });
  }
}
